/**
 * Display helpers for the Forms module — human labels for lookup codes, status /
 * source metadata, relative times, people's names and initials.
 *
 * The lookup tables store code-like names ("PendingApproval", "ExternalApi"); the UI
 * always goes through these helpers so people read "Pending approval" / "External API".
 */

import type { FormEntryListItemView } from '@actschurch/ccms-sdk';
import { stampToDate, readKey } from './formsUtils';

/** "PendingApproval" → "Pending approval", "ExternalApi" → "External API", "manage_link_sent" → "Manage link sent". */
export function humanize(value: string | null | undefined): string {
  if (!value) return '';
  const spaced = value
    .replace(/[_.-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  const fixed = spaced.replace(/\bapi\b/g, 'API').replace(/\bqr\b/g, 'QR').replace(/\bid\b/g, 'ID');
  return fixed.charAt(0).toUpperCase() + fixed.slice(1);
}

export interface StatusMeta {
  label: string;
  color: string;
  /** Short hint shown in tooltips / empty states. */
  hint?: string;
}

export const FORM_STATUS_META: Record<number, StatusMeta> = {
  1: { label: 'Draft', color: 'gray', hint: 'Not published yet — the link shows a "not published" message.' },
  2: { label: 'Live', color: 'green', hint: 'Published and accepting entries within its open window.' },
  3: { label: 'Closed', color: 'orange', hint: 'No longer accepting entries.' },
  4: { label: 'Archived', color: 'dark', hint: 'Hidden from default lists.' },
};

export const ENTRY_STATUS_META: Record<number, StatusMeta> = {
  1: { label: 'Submitted', color: 'blue', hint: 'Received — nothing else to do.' },
  2: { label: 'Pending approval', color: 'yellow', hint: 'Waiting for a reviewer to approve or reject.' },
  3: { label: 'Pending payment', color: 'orange', hint: 'Waiting for payment to complete.' },
  4: { label: 'Confirmed', color: 'green', hint: 'Place confirmed.' },
  5: { label: 'Waitlisted', color: 'grape', hint: 'On the waitlist until a place frees up.' },
  6: { label: 'Offered', color: 'cyan', hint: 'A place was offered — waiting for the person to accept.' },
  7: { label: 'Rejected', color: 'red', hint: 'Rejected by a reviewer.' },
  8: { label: 'Cancelled', color: 'gray', hint: 'Cancelled — the place was released.' },
  9: { label: 'Expired', color: 'dark', hint: 'Hold or offer ran out.' },
};

export const PAYMENT_STATUS_META: Record<number, StatusMeta> = {
  1: { label: 'Not required', color: 'gray' },
  2: { label: 'Awaiting payment', color: 'orange' },
  3: { label: 'Paid', color: 'green' },
  4: { label: 'Failed', color: 'red' },
  5: { label: 'Cancelled', color: 'gray' },
  6: { label: 'Refunded', color: 'violet' },
  7: { label: 'Waived', color: 'teal' },
};

export const ENTRY_SOURCE_META: Record<number, StatusMeta> = {
  1: { label: 'Public link', color: 'blue' },
  2: { label: 'Member portal', color: 'indigo' },
  3: { label: 'External API', color: 'violet' },
  4: { label: 'Inbound token', color: 'teal' },
  5: { label: 'Import', color: 'gray' },
  6: { label: 'Added by staff', color: 'orange' },
};

export const entryStatusLabel = (id: number | null | undefined, name?: string | null) =>
  (id != null && ENTRY_STATUS_META[id]?.label) || humanize(name) || (id ? `Status ${id}` : '—');
export const formStatusLabel = (id: number | null | undefined, name?: string | null) =>
  (id != null && FORM_STATUS_META[id]?.label) || humanize(name) || (id ? `Status ${id}` : '—');
export const paymentStatusLabel = (id: number | null | undefined, name?: string | null) =>
  (id != null && PAYMENT_STATUS_META[id]?.label) || humanize(name) || (id ? `Status ${id}` : '—');
export const sourceLabel = (id: number | null | undefined, name?: string | null) =>
  (id != null && ENTRY_SOURCE_META[id]?.label) || humanize(name) || '—';

// ---------------------------------------------------------------- history

const HISTORY_LABELS: Record<string, string> = {
  submitted: 'Submitted',
  approve: 'Approved',
  approved: 'Approved',
  reject: 'Rejected',
  rejected: 'Rejected',
  cancel: 'Cancelled',
  cancelled: 'Cancelled',
  confirm: 'Confirmed',
  confirmed: 'Confirmed',
  promote: 'Promoted from the waitlist',
  promoted: 'Promoted from the waitlist',
  offer: 'Place offered',
  offered: 'Place offered',
  expire: 'Expired',
  expired: 'Expired',
  deleted: 'Deleted',
  delete: 'Deleted',
  edited: 'Answers edited',
  payment_waived: 'Balance waived',
  payment_needs_review: 'Payment needs review',
  paymentcompletedevent: 'Payment received',
  manage_link_sent: 'Manage link emailed',
  resend: 'Confirmation resent',
  resendconfirmation: 'Confirmation resent',
  confirmation_resent: 'Confirmation email sent again',
  linked: 'Linked to a member',
  merged: 'Merged into a member',
  created: 'Created as a member',
  converted: 'Created as a member',
  family_linked: 'Family linked',
  retention_purged: 'Personal details removed (retention)',
};

export const historyActionLabel = (action: string | null | undefined) =>
  (action && HISTORY_LABELS[action.toLowerCase()]) || humanize(action) || 'Updated';

/** Colour for a history row: the status it moved to, else a neutral tone. */
export function historyColor(action: string | null | undefined, toStatusID?: number | null): string {
  const a = (action || '').toLowerCase();
  if (a === 'deleted' || a === 'delete' || a.startsWith('reject')) return 'red';
  if (a.startsWith('payment')) return 'teal';
  if (a === 'linked' || a === 'merged' || a === 'created' || a === 'converted' || a === 'family_linked') return 'grape';
  if (toStatusID != null && ENTRY_STATUS_META[toStatusID]) return ENTRY_STATUS_META[toStatusID].color;
  return 'gray';
}

export function actorLabel(actorType?: string | null, actorName?: string | null): string {
  if (actorName) return actorName;
  switch ((actorType || '').toLowerCase()) {
    case 'public':
      return 'the registrant (public form)';
    case 'registrant':
      return 'the registrant (manage link)';
    case 'apikey':
      return 'an API key';
    case 'inbound':
      return 'an inbound token';
    case 'system':
      return 'the system';
    case 'user':
      return 'a staff member';
    default:
      return humanize(actorType) || 'someone';
  }
}

// ---------------------------------------------------------------- time

const DAY = 86_400_000;

/** "just now", "5 min ago", "3 hours ago", "yesterday", "4 days ago", "in 6 days", "12 Oct". */
export function relativeTime(stamp: number | string | null | undefined, fallback = '—'): string {
  const d = stampToDate(stamp);
  if (!d) return fallback;
  const diff = d.getTime() - Date.now();
  const abs = Math.abs(diff);
  const future = diff > 0;
  if (abs < 60_000) return future ? 'in a moment' : 'just now';
  if (abs < 3_600_000) {
    const m = Math.round(abs / 60_000);
    return future ? `in ${m} min` : `${m} min ago`;
  }
  if (abs < DAY) {
    const h = Math.round(abs / 3_600_000);
    return future ? `in ${h} hour${h === 1 ? '' : 's'}` : `${h} hour${h === 1 ? '' : 's'} ago`;
  }
  const days = Math.round(abs / DAY);
  if (days === 1) return future ? 'tomorrow' : 'yesterday';
  if (days < 31) return future ? `in ${days} days` : `${days} days ago`;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: d.getFullYear() === new Date().getFullYear() ? undefined : 'numeric' });
}

/** Whole days from now until the stamp (negative = past). */
export function daysUntil(stamp: number | string | null | undefined): number | null {
  const d = stampToDate(stamp);
  if (!d) return null;
  return Math.ceil((d.getTime() - Date.now()) / DAY);
}

/** Closing info for a form: label + urgency colour. */
export function closingInfo(closesAt: number | null | undefined): { label: string; color: string } | null {
  const n = daysUntil(closesAt);
  if (n === null) return null;
  if (n < 0) return { label: `Closed ${relativeTime(closesAt)}`, color: 'gray' };
  if (n === 0) return { label: 'Closes today', color: 'red' };
  if (n <= 3) return { label: `Closes in ${n} day${n === 1 ? '' : 's'}`, color: 'red' };
  if (n <= 14) return { label: `Closes in ${n} days`, color: 'orange' };
  return { label: `Closes ${relativeTime(closesAt)}`, color: 'gray' };
}

// ---------------------------------------------------------------- people

const NAME_KEYS = ['fullName', 'full_name', 'name', 'yourName', 'participantName', 'contactName'];

/**
 * Best display name for an entry: the server's projection (first name + surname), else a
 * name-like answer ("fullName"…), else the email, else "Anonymous response" (surveys) —
 * callers show the reference alongside.
 */
export function entryPersonName(
  entry: Pick<FormEntryListItemView, 'displayName' | 'firstName' | 'surname' | 'email' | 'publicRef' | 'answers'>
): string {
  const projected = (entry.displayName || [entry.firstName, entry.surname].filter(Boolean).join(' ')).trim();
  if (projected) return projected;
  const answers = entry.answers as Record<string, unknown> | null | undefined;
  for (const key of NAME_KEYS) {
    const v = readKey<unknown>(answers, key);
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return entry.email || 'Anonymous response';
}

export function initials(name: string | null | undefined): string {
  const parts = (name || '').replace(/[^\p{L}\s'-]/gu, ' ').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const AVATAR_COLORS = ['blue', 'cyan', 'teal', 'green', 'lime', 'yellow', 'orange', 'red', 'pink', 'grape', 'violet', 'indigo'];

/** Stable colour per name, so the same person always gets the same avatar colour. */
export function colorFor(key: string | null | undefined): string {
  let h = 0;
  for (const ch of key || '') h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

// ---------------------------------------------------------------- numbers

export const pct = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 1000) / 10 : 0);

export const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;
